import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type { IAppState } from '~/appstate';
import * as Hub from '../ogm-hub';

export type IDiscontinuedAlertState = EntityState<Hub.DiscontinuedAlert>;
export const discontinuedAlertAdapter =
	createEntityAdapter<Hub.DiscontinuedAlert>();

export interface IGetDiscontinuedAlertsArgs {
	supplierId?: number;
	filterType?: 'isBoosted' | 'wasCoverageLost' | 'metaProductId';
	filterValue?: boolean | number;
	sortType?:
		| 'discontinuedDate'
		| 'distributionCenter'
		| 'masterItemName'
		| 'description'
		| 'brand'
		| 'packSize'
		| 'wasCoverageLost'
		| 'orderedQuantity';
	sortOrder?: 'ascending' | 'descending' | 'asc' | 'desc';
	pageIndex: number;
	pageSize: number;
}

export function convertFilterValue(
	filterValue?: boolean | number,
	filterType?: 'isBoosted' | 'wasCoverageLost' | 'metaProductId'
): string {
	if (!filterType) {
		return 'false';
	}
	if (filterType === 'metaProductId') {
		return `${filterValue}`;
	} else {
		return 'true';
	}
}

export function convertSortOrder(
	sortOrder?: 'ascending' | 'descending' | 'asc' | 'desc'
): 'asc' | 'desc' {
	if (sortOrder === 'descending' || !sortOrder) {
		return 'desc';
	}
	if (sortOrder === 'ascending') {
		return 'asc';
	}
	return sortOrder;
}

export interface FetchDiscontinuedAlertsResult
	extends Hub.ResultSet<Hub.DiscontinuedAlert> {
	pageIndex: number;
}
export const fetchDiscontinuedAlerts = createAsyncThunk<
	FetchDiscontinuedAlertsResult,
	IGetDiscontinuedAlertsArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'discontinuedAlerts/fetchDiscontinuedAlerts',
	async (params: IGetDiscontinuedAlertsArgs, { rejectWithValue }) => {
		try {
			const supplierId = params.supplierId ?? 0;
			const filterType = params.filterType ?? '';
			const filterValue = convertFilterValue(
				params.filterValue,
				params.filterType
			);
			const sortType = params.sortType ?? '';
			const sortOrder = convertSortOrder(params.sortOrder);
			let pageIndex = Math.max(0, params.pageIndex);
			const pageSize = Math.max(1, params.pageSize);

			let result = await unwrapFetch2(
				Hub.DiscontinuedAlertsController.Get(
					supplierId,
					filterType,
					filterValue,
					sortType,
					sortOrder,
					pageIndex,
					pageSize
				)
			);

			if (
				result.alerts.totalPages > 0 &&
				result.alerts.totalPages <= pageIndex
			) {
				pageIndex = Math.max(0, result.alerts.totalPages - 1);
				result = await unwrapFetch2(
					Hub.DiscontinuedAlertsController.Get(
						supplierId,
						filterType,
						filterValue,
						sortType,
						sortOrder,
						pageIndex,
						pageSize
					)
				);
			}

			return {
				...result.alerts,
				pageIndex,
			};
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const discontinuedAlertsSlice = createSlice({
	name: 'discontinuedAlerts',
	initialState: discontinuedAlertAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchDiscontinuedAlerts.fulfilled, (state, action) => {
			const alerts: Hub.DiscontinuedAlert[] = action.payload.results;
			discontinuedAlertAdapter.upsertMany(state, alerts);
		});
	},
});

export function selectAlertById(
	state: IAppState,
	id: number
): Hub.DiscontinuedAlert {
	return state.discontinuedAlerts.entities[+id];
}

export function selectAlertsById(
	state: IAppState,
	ids: number[]
): Hub.DiscontinuedAlert[] {
	return ids.map((id) => selectAlertById(state, id));
}
